import { useCallback, useEffect, useMemo, useRef } from 'react'
import { createNadDebug } from '../../../utils/debugLog'
import type { AudioEditClip } from './audioEditTypes'
import {
  getAudioEditDurationS,
  getAudioEditSourceTimeS,
  MIN_AUDIO_EDIT_SPEED,
} from './audioEditMath'

const debug = createNadDebug('dw:timeline-playback:audio-pool')
const globalMuteLog = createNadDebug('dw:videoeditor-canvas-preview:audio-pool')
const MAX_DRIFT_SECONDS = 0.1
let debugHandleSeq = 0

declare global {
  interface HTMLMediaElement {
    webkitPreservesPitch?: boolean
  }
  interface Window {
    webkitAudioContext?: typeof AudioContext
    /**
     * Dev-only debug handle: exposes the live GainNode for each pooled clip
     * so Playwright specs and ad-hoc devtools sessions can read the effective
     * amplitude. Gated by import.meta.env.DEV in production builds — set
     * window.__NAD_DEBUG__ = true at runtime to force-enable for E2E tests.
     */
    __nadAudioPoolGains?: Map<string, GainNode>
    __nadAudioPoolElements?: Map<string, HTMLMediaElement>
    __NAD_DEBUG__?: boolean
  }
}

/**
 * Per-clip WebAudio chain.
 *
 * `clip.volume` is a *linear amplitude* (0..~15.85) — the timeline volume
 * slider's 0..200% range maps positions > 1.0 via positionToAmplitude() with
 * up to +24 dB of boost (see panels/timeline/volumeMapping.ts). The HTML
 * <audio> element's `.volume` property is spec-capped at [0, 1] and throws
 * `IndexSizeError` for anything outside that range. To realize the boost
 * audibly *and* avoid the DOMException, we route each pooled <audio> through
 *
 *   MediaElementAudioSourceNode -> GainNode -> ctx.destination
 *
 * exactly like the Timeline Modal's VideoPreviewPlayer does for video. The
 * element's own `.volume` stays pinned at 1.0 (full headroom for the gain to
 * scale), and `gain.gain.value` carries the unbounded amplitude.
 *
 * `createMediaElementSource(el)` may only be called ONCE per element, so the
 * WebAudio chain is built when the element is first added to the pool and
 * torn down only when the entry is removed (clip deleted, fingerprint
 * change, or pool unmount). Per-tick volume updates write to the GainNode,
 * never rebuild the chain.
 *
 * Fallback: if `AudioContext` is unavailable or `createMediaElementSource`
 * throws (rare — e.g. test browsers without WebAudio, or an element already
 * captured by another graph), we log via `debug(...)` and fall back to
 * `element.volume = Math.max(0, Math.min(1, clip.volume))`. The clamp lives
 * ONLY on this fallback path; the happy path is unbounded.
 */
interface AudioPoolEntry {
  element: HTMLMediaElement
  clipId: string
  debugHandleId: string
  audioCtx: AudioContext | null
  source: MediaElementAudioSourceNode | null
  gain: GainNode | null
  /** False when the ctx is a caller-owned shared context (do not close on teardown). */
  ownsContext: boolean
  /** Blob URL used to build this element; revision changes replace the entry. */
  resolvedSrc: string
}

interface UseAudioClipPoolProps {
  clips: AudioEditClip[]
  playheadPosition: number
  isPlaying: boolean
  resolveClipUrl: (clip: AudioEditClip) => string | null
  /**
   * When true, every pooled <audio> element is forced to muted=true regardless
   * of per-clip mute. Used by the canvas preview's global mute button.
   */
  globalMuted?: boolean
  /**
   * Optional per-tick amplitude override (RFC-0986 lane preview). When provided,
   * the GainNode is driven by `computeClipGain(clip, playheadPositionS)` instead
   * of the static `clip.volume` — this is how the unified-audio editor folds
   * lane volume, fades, and speech-window ducking into a time-varying gain.
   * MUST be referentially stable (memoized) or the sync effect re-runs every
   * render. Omitting it leaves the static-volume behavior byte-identical, so
   * existing callers (timeline / canvas preview) are unaffected.
   */
  computeClipGain?: (clip: AudioEditClip, playheadPositionS: number) => number
  /**
   * Optional caller-owned AudioContext shared by every pooled clip (RFC-0986).
   * Passing one lets the caller create + resume a SINGLE context inside the Play
   * user-gesture, so clips whose blobs finish loading AFTER the gesture still
   * play on Safari/Firefox (where resuming a late-created per-clip context from
   * an effect is not a valid user activation). When omitted, each clip creates
   * and owns its own context exactly as before (default-preserving).
   */
  sharedAudioContext?: AudioContext | null
  /** Changes when same-path media resolves to a new blob URL. */
  mediaRevision?: number
  /**
   * Minimum speed used by timeline/source-time math. Legacy callers retain
   * their historical 0.1 floor; validated V2 compositions pass 0 so preview
   * timing matches the exact positive render speed.
   */
  minimumPlaybackSpeed?: number
}

export interface AudioClipPoolHandle {
  resumeAudioContexts: () => void
}

function setPitchPreservation(element: HTMLMediaElement, preservePitch: boolean) {
  element.preservesPitch = preservePitch
  element.webkitPreservesPitch = preservePitch
}

/**
 * A source served as a VIDEO container (.mp4/.mov/.webm/.m4v) — e.g. RFC-1088's native
 * "Original audio" lane, whose clip is the raw variant .mp4 (find_original_video_path). An
 * `<audio>` element cannot decode/surface a video container's audio track, so those beds played
 * SILENTLY in the lanes preview. Detected off the ORIGINAL `clip.sourcePath` (the blob URL fed to
 * `element.src` has no extension). Query-string / fragment tolerant.
 */
function isVideoContainer(sourcePath: string | undefined): boolean {
  if (!sourcePath) return false
  const path = sourcePath.split('?')[0].split('#')[0].toLowerCase()
  return (
    path.endsWith('.mp4') ||
    path.endsWith('.mov') ||
    path.endsWith('.webm') ||
    path.endsWith('.m4v')
  )
}

function shouldExposeGainHandle(): boolean {
  // Vite stamps import.meta.env.DEV true in dev builds; tests can flip
  // window.__NAD_DEBUG__ at runtime to force-enable in prod-build E2E.
  try {
    if (typeof window !== 'undefined' && window.__NAD_DEBUG__) return true
  } catch {
    /* noop */
  }
  try {
    return Boolean(import.meta.env?.DEV)
  } catch {
    return false
  }
}

function registerGainHandle(clipId: string, gain: GainNode): void {
  if (!shouldExposeGainHandle()) return
  if (typeof window === 'undefined') return
  if (!window.__nadAudioPoolGains) {
    window.__nadAudioPoolGains = new Map<string, GainNode>()
  }
  window.__nadAudioPoolGains.set(clipId, gain)
}

function registerElementHandle(clipId: string, element: HTMLMediaElement): void {
  if (!shouldExposeGainHandle()) return
  if (typeof window === 'undefined') return
  if (!window.__nadAudioPoolElements) {
    window.__nadAudioPoolElements = new Map<string, HTMLMediaElement>()
  }
  window.__nadAudioPoolElements.set(clipId, element)
}

function unregisterGainHandle(clipId: string): void {
  if (typeof window === 'undefined' || !window.__nadAudioPoolGains) return
  window.__nadAudioPoolGains.delete(clipId)
}

function unregisterElementHandle(clipId: string): void {
  if (typeof window === 'undefined' || !window.__nadAudioPoolElements) return
  window.__nadAudioPoolElements.delete(clipId)
}

/**
 * Build the per-clip <audio> element and its WebAudio chain. Returns a
 * fully-populated AudioPoolEntry; on WebAudio failure the entry's audioCtx
 * /source/gain are null and the caller's per-tick sync path falls back to
 * the clamped HTMLMediaElement.volume write.
 */
function createPoolEntry(
  clip: AudioEditClip,
  src: string,
  globalMuted: boolean,
  sharedAudioContext?: AudioContext | null,
): AudioPoolEntry {
  const debugHandleId = `${clip.id}:${debugHandleSeq += 1}`
  // RFC-1088 (BUG 1): the native "Original audio" lane's source is the raw variant .mp4 (a VIDEO
  // container). An <audio> element cannot surface a video container's audio track, so the bed
  // played SILENTLY. Create a <video> element for video-container sources so the muxed audio flows
  // through the SAME MediaElementAudioSourceNode -> gain -> destination chain (the element is never
  // attached to the DOM, so nothing renders — only its audio plays). Audio sources keep the
  // byte-identical <audio> path (INV-2 — no change for timeline/canvas audio clips).
  const isVideo = isVideoContainer(clip.sourcePath)
  const element: HTMLMediaElement = isVideo
    ? document.createElement('video')
    : document.createElement('audio')
  if (isVideo) {
    debug({ event: 'audio-pool-video-element', clipId: clip.id, sourcePath: clip.sourcePath })
  }
  element.preload = 'auto'
  element.src = src
  element.playbackRate = clip.speed
  element.muted = globalMuted
  setPitchPreservation(element, clip.preservePitch)
  registerElementHandle(debugHandleId, element)

  let audioCtx: AudioContext | null = sharedAudioContext ?? null
  // A shared context is caller-owned; only close a context this entry created.
  let ownsContext = false
  let source: MediaElementAudioSourceNode | null = null
  let gain: GainNode | null = null

  try {
    if (!audioCtx) {
      const AudioContextCtor =
        typeof window !== 'undefined'
          ? window.AudioContext || window.webkitAudioContext
          : undefined
      if (!AudioContextCtor) {
        debug({ event: 'audio-pool-webaudio-unsupported', clipId: clip.id })
        element.volume = Math.max(0, Math.min(1, clip.volume))
        return {
          element,
          clipId: clip.id,
          debugHandleId,
          audioCtx: null,
          source: null,
          gain: null,
          ownsContext: false,
          resolvedSrc: src,
        }
      }
      audioCtx = new AudioContextCtor()
      ownsContext = true
    }
    source = audioCtx.createMediaElementSource(element)
    gain = audioCtx.createGain()
    source.connect(gain).connect(audioCtx.destination)
    // GainNode owns the final amplitude; the element's own volume must stay
    // at 1.0 so the gain has full headroom up to +24 dB (positions > 100%
    // on the slider).
    element.volume = 1.0
    gain.gain.value = Math.max(0, clip.volume)
    if (audioCtx.state === 'suspended') {
      audioCtx.resume().catch(() => {
        /* autoplay policy — play() from a user gesture will unblock */
      })
    }
    registerGainHandle(debugHandleId, gain)
    debug({
      event: 'audio-pool-gain-applied',
      clipId: clip.id,
      clipVolume: clip.volume,
      gainValue: gain.gain.value,
      viaGain: true,
    })
  } catch (err) {
    debug({
      event: 'audio-pool-webaudio-setup-failed',
      clipId: clip.id,
      error: err instanceof Error ? err.message : String(err),
    })
    // Tear down anything that did succeed before falling back.
    try {
      source?.disconnect()
    } catch {
      /* noop */
    }
    try {
      gain?.disconnect()
    } catch {
      /* noop */
    }
    // Only close a context this entry created — never the caller's shared one.
    if (ownsContext) {
      audioCtx?.close().catch(() => {
        /* noop */
      })
    }
    audioCtx = null
    ownsContext = false
    source = null
    gain = null
    element.volume = Math.max(0, Math.min(1, clip.volume))
  }

  return { element, clipId: clip.id, debugHandleId, audioCtx, source, gain, ownsContext, resolvedSrc: src }
}

function teardownPoolEntry(entry: AudioPoolEntry): void {
  entry.element.pause()
  entry.element.src = ''
  unregisterGainHandle(entry.debugHandleId)
  unregisterElementHandle(entry.debugHandleId)
  try {
    entry.source?.disconnect()
  } catch {
    /* noop */
  }
  try {
    entry.gain?.disconnect()
  } catch {
    /* noop */
  }
  // A caller-owned shared context outlives its entries — never close it here.
  if (entry.ownsContext) {
    entry.audioCtx?.close().catch(() => {
      /* noop */
    })
  }
}

export function useAudioClipPool({
  clips,
  playheadPosition,
  isPlaying,
  resolveClipUrl,
  globalMuted = false,
  computeClipGain,
  sharedAudioContext,
  mediaRevision = 0,
  minimumPlaybackSpeed = MIN_AUDIO_EDIT_SPEED,
}: UseAudioClipPoolProps): AudioClipPoolHandle {
  const poolRef = useRef<Map<string, AudioPoolEntry>>(new Map())
  const fingerprint = useMemo(
    () => `${mediaRevision}|${clips.map((c) => `${c.id}:${c.sourcePath ?? c.sourceUrl ?? ''}:${c.muted}`).join('|')}`,
    [clips, mediaRevision],
  )
  const fingerprintRef = useRef('')
  const lastGlobalMutedRef = useRef<boolean | null>(null)

  const resumeAudioContexts = useCallback(() => {
    const pool = poolRef.current
    for (const [, entry] of pool) {
      if (!entry.audioCtx || entry.audioCtx.state === 'closed') continue
      void entry.audioCtx.resume().catch(() => {
        /* autoplay policy can still reject outside a user gesture */
      })
    }
  }, [])

  // Apply global mute to every live element.
  useEffect(() => {
    const pool = poolRef.current
    let appliedCount = 0
    for (const [, entry] of pool) {
      if (entry.element.muted !== globalMuted) {
        entry.element.muted = globalMuted
        appliedCount += 1
      }
    }
    if (lastGlobalMutedRef.current !== globalMuted) {
      globalMuteLog({
        event: 'global-mute-applied',
        muted: globalMuted,
        elementsTouched: appliedCount,
        totalElements: pool.size,
      })
      lastGlobalMutedRef.current = globalMuted
    }
  }, [globalMuted, clips])

  useEffect(() => {
    if (fingerprintRef.current === fingerprint) return
    fingerprintRef.current = fingerprint

    const pool = poolRef.current
    const currentClipIds = new Set(clips.map((clip) => clip.id))

    for (const [clipId, entry] of pool) {
      if (!currentClipIds.has(clipId)) {
        debug({ event: 'audio-pool-remove', clipId })
        teardownPoolEntry(entry)
        pool.delete(clipId)
      }
    }

    for (const clip of clips) {
      if (clip.muted) {
        const existing = pool.get(clip.id)
        if (existing) {
          teardownPoolEntry(existing)
          pool.delete(clip.id)
        }
        continue
      }

      if (!pool.has(clip.id)) {
        const src = resolveClipUrl(clip)
        if (!src) continue
        pool.set(clip.id, createPoolEntry(clip, src, globalMuted, sharedAudioContext))
      }
    }
  }, [clips, fingerprint, resolveClipUrl, globalMuted, sharedAudioContext])

  useEffect(() => {
    const pool = poolRef.current
    for (const clip of clips) {
      const src = resolveClipUrl(clip)
      const existing = pool.get(clip.id)
      if (clip.muted || !src) {
        if (existing) {
          teardownPoolEntry(existing)
          pool.delete(clip.id)
        }
        continue
      }
      if (existing?.resolvedSrc === src) continue
      if (existing) {
        teardownPoolEntry(existing)
        pool.delete(clip.id)
      }
      pool.set(clip.id, createPoolEntry(clip, src, globalMuted, sharedAudioContext))
    }
  }, [clips, resolveClipUrl, globalMuted, sharedAudioContext, mediaRevision])

  useEffect(() => {
    const pool = poolRef.current

    for (const clip of clips) {
      if (clip.muted) continue
      const entry = pool.get(clip.id)
      if (!entry) continue

      const { element, gain } = entry
      if (gain) registerGainHandle(clip.id, gain)
      const clipEnd =
        clip.timelineStartS + getAudioEditDurationS(clip, minimumPlaybackSpeed)
      const isInRange = playheadPosition >= clip.timelineStartS && playheadPosition < clipEnd

      if (!isInRange) {
        if (!element.paused) element.pause()
        continue
      }

      // Amplitude routing: when WebAudio is wired (the happy path), the
      // GainNode owns the final volume — `element.volume` stays at 1.0 so
      // the gain has full headroom for the +24 dB boost. When the chain
      // failed to build (rare; old test browsers, captured elements),
      // fall back to clamped HTMLMediaElement.volume. Never mix the two:
      // mixing would double-attenuate when both are < 1.
      // `computeClipGain` (RFC-0986) lets the unified-audio preview fold lane
      // volume / fades / ducking into a time-varying gain; without it the static
      // clip.volume path is unchanged.
      const baseAmp = computeClipGain ? computeClipGain(clip, playheadPosition) : clip.volume
      const targetAmp = globalMuted ? 0 : Math.max(0, baseAmp)
      if (gain) {
        if (gain.gain.value !== targetAmp) {
          gain.gain.value = targetAmp
        }
      } else {
        const elTarget = Math.max(0, Math.min(1, targetAmp))
        if (element.volume !== elTarget) element.volume = elTarget
      }
      if (element.playbackRate !== clip.speed) element.playbackRate = clip.speed
      if (element.muted !== globalMuted) element.muted = globalMuted
      setPitchPreservation(element, clip.preservePitch)

      const expectedTime = getAudioEditSourceTimeS(
        clip,
        playheadPosition,
        minimumPlaybackSpeed,
      )
      if (Math.abs(element.currentTime - expectedTime) > MAX_DRIFT_SECONDS) {
        debug({
          event: 'audio-pool-drift-correction',
          clipId: clip.id,
          currentTimeS: Number(element.currentTime.toFixed(3)),
          expectedTimeS: Number(expectedTime.toFixed(3)),
          speed: Number(clip.speed.toFixed(3)),
        })
        element.currentTime = expectedTime
      }

      if (isPlaying && element.paused) {
        if (entry.audioCtx && entry.audioCtx.state !== 'closed') {
          void entry.audioCtx.resume().catch(() => {
            /* play() below will report the actionable media error */
          })
        }
        element.play().catch((err) => {
          if (err.name !== 'AbortError') {
            console.error('Audio play failed:', err)
          }
        })
      } else if (!isPlaying && !element.paused) {
        element.pause()
      }
    }
  }, [
    clips,
    playheadPosition,
    isPlaying,
    globalMuted,
    computeClipGain,
    minimumPlaybackSpeed,
  ])

  useEffect(() => {
    const pool = poolRef.current
    return () => {
      for (const [, entry] of pool) {
        teardownPoolEntry(entry)
      }
      pool.clear()
    }
  }, [])

  return useMemo(() => ({ resumeAudioContexts }), [resumeAudioContexts])
}
